import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { instructionPromptApi } from 'hooks/api/instructionPrompt'
import { NewDate } from 'lib/helper/day'

type FormState = {
  promptTemplate: string
  recentSituationDesc: string
  nextCheckPointsDesc: string
  longTermTrendDesc: string
}

const INITIAL_FORM: FormState = {
  promptTemplate: '',
  recentSituationDesc: '',
  nextCheckPointsDesc: '',
  longTermTrendDesc: '',
}

export function PromptSettingSection() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM)
  const [originalForm, setOriginalForm] = useState<FormState>(INITIAL_FORM)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    const fetchPrompt = async () => {
      try {
        const res = await instructionPromptApi().getCurrent()
        const data = {
          promptTemplate: res.data.promptTemplate,
          recentSituationDesc: res.data.recentSituationDesc,
          nextCheckPointsDesc: res.data.nextCheckPointsDesc,
          longTermTrendDesc: res.data.longTermTrendDesc,
        }
        setForm(data)
        setOriginalForm(data)
        setLastUpdatedAt(res.data.savedAt)
      } catch {
        setSnackbar({
          open: true,
          message: 'プロンプトの取得に失敗しました',
          severity: 'error',
        })
      } finally {
        setLoading(false)
      }
    }
    fetchPrompt()
  }, [])

  const handleSave = async () => {
    if (!form.promptTemplate.trim()) {
      setSnackbar({
        open: true,
        message: 'プロンプトを入力してください',
        severity: 'error',
      })
      return
    }

    setSaving(true)
    try {
      await instructionPromptApi().create(form)
      const res = await instructionPromptApi().getCurrent()
      const data = {
        promptTemplate: res.data.promptTemplate,
        recentSituationDesc: res.data.recentSituationDesc,
        nextCheckPointsDesc: res.data.nextCheckPointsDesc,
        longTermTrendDesc: res.data.longTermTrendDesc,
      }
      setForm(data)
      setOriginalForm(data)
      setLastUpdatedAt(res.data.savedAt)
      setSnackbar({
        open: true,
        message: '保存しました',
        severity: 'success',
      })
    } catch {
      setSnackbar({
        open: true,
        message: '保存に失敗しました',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(form.promptTemplate)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setSnackbar({
        open: true,
        message: 'コピーに失敗しました',
        severity: 'error',
      })
    }
  }

  const hasChanges =
    form.promptTemplate !== originalForm.promptTemplate ||
    form.recentSituationDesc !== originalForm.recentSituationDesc ||
    form.nextCheckPointsDesc !== originalForm.nextCheckPointsDesc ||
    form.longTermTrendDesc !== originalForm.longTermTrendDesc

  return (
    <>
      <Accordion sx={accordionStyle}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={summaryStyle}>
            <Typography variant="h6" fontWeight="bold">
              プロンプト設定
            </Typography>
            {hasChanges && (
              <Typography variant="caption" color="warning.main" sx={{ ml: 2 }}>
                ※ 未保存の変更があります
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box sx={loadingStyle}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={contentStyle}>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={infoStyle}
              >
                <p style={{ margin: 0, marginBottom: 8 }}>
                  AIが巡察サマリを生成する際に使用するプロンプトです。
                  保存すると履歴として保持され、次回のサマリ生成から新しいプロンプトが適用されます。
                </p>
                <p style={{ margin: 0, fontWeight: 500 }}>
                  【実際のプロンプト構成】
                </p>
                <ol style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                  <li>このプロンプト</li>
                  <li>警備員情報（名前・入社日）</li>
                  <li>巡察記録（直近の記録内容）</li>
                </ol>
              </Typography>

              {lastUpdatedAt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={timestampStyle}
                >
                  最終更新:{' '}
                  {NewDate(lastUpdatedAt)
                    .tz('Asia/Tokyo')
                    .format('YYYY-MM-DD HH:mm:ss')}
                </Typography>
              )}

              {/* メインプロンプト */}
              <Typography variant="subtitle2" fontWeight="bold">
                プロンプトテンプレート
              </Typography>
              <Box sx={textareaContainerStyle}>
                <TextField
                  multiline
                  rows={8}
                  fullWidth
                  value={form.promptTemplate}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      promptTemplate: e.target.value,
                    }))
                  }
                  placeholder="プロンプトテンプレートを入力..."
                  sx={textareaStyle}
                />
                <Tooltip
                  title={copied ? 'コピーしました！' : 'クリップボードにコピー'}
                >
                  <IconButton onClick={handleCopy} sx={copyButtonStyle}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Tool Useスキーマ説明 */}
              <Typography variant="subtitle2" fontWeight="bold">
                出力フィールドの説明（Tool Use）
              </Typography>
              <Typography variant="caption" color="text.secondary">
                AIが各フィールドに出力する内容を制御します。文字数制限や例文を指定できます。
              </Typography>

              <TextField
                label="直近の状況"
                fullWidth
                value={form.recentSituationDesc}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    recentSituationDesc: e.target.value,
                  }))
                }
                placeholder="例: 【必ず60文字以内】直近の状況を1文で。"
                size="small"
              />

              <TextField
                label="次回確認事項"
                fullWidth
                value={form.nextCheckPointsDesc}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    nextCheckPointsDesc: e.target.value,
                  }))
                }
                placeholder="例: 【必ず60文字以内】次回確認事項を1文で。"
                size="small"
              />

              <TextField
                label="長期傾向"
                fullWidth
                value={form.longTermTrendDesc}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    longTermTrendDesc: e.target.value,
                  }))
                }
                placeholder="例: 【必ず100文字以内】長期傾向を2文で。"
                size="small"
              />

              <Box sx={buttonContainerStyle}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                >
                  {saving ? '保存中...' : '保存'}
                </Button>
                {saving && <CircularProgress size={16} />}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

const accordionStyle: SxProps = {
  mb: 3,
  '&:before': { display: 'none' },
  boxShadow: 1,
}

const summaryStyle: SxProps = {
  display: 'flex',
  alignItems: 'center',
}

const loadingStyle: SxProps = {
  display: 'flex',
  justifyContent: 'center',
  py: 2,
}

const contentStyle: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}

const infoStyle: SxProps = {
  backgroundColor: '#f5f5f5',
  padding: 2,
  borderRadius: 1,
}

const timestampStyle: SxProps = {
  textAlign: 'right',
}

const textareaContainerStyle: SxProps = {
  position: 'relative',
}

const textareaStyle: SxProps = {
  '& .MuiInputBase-input': {
    fontFamily: 'monospace',
    fontSize: 14,
    lineHeight: 1.6,
  },
}

const copyButtonStyle: SxProps = {
  position: 'absolute',
  top: 8,
  right: 8,
}

const buttonContainerStyle: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
}
